import { InvalidCatalogDefinitionError } from "./errors.model";
import { asMemberId } from "./member-identity";
import type { TreatmentParameter } from "./treatment-definition.model";

export type StandardParameterKey = "reps" | "sets" | "duration";
// Permanent reserved identities: never change or reuse these ids. Authored
// parameters use prm and cannot mint identities in the stdprm namespace.
// Duration means time per set.
export const STANDARD_PARAMETERS = Object.freeze({
  reps: Object.freeze({
    id: asMemberId("stdprm-1"),
    label: "Reps",
    value: Object.freeze({ kind: "Integer", min: 1 }),
  }),
  sets: Object.freeze({
    id: asMemberId("stdprm-2"),
    label: "Sets",
    value: Object.freeze({ kind: "Integer", min: 1 }),
  }),
  duration: Object.freeze({
    id: asMemberId("stdprm-3"),
    label: "Duration",
    value: Object.freeze({
      kind: "Quantity",
      display: "duration",
      unit: Object.freeze({ code: "s", display: "seconds" }),
      min: 1,
    }),
  }),
} satisfies Readonly<Record<StandardParameterKey, TreatmentParameter>>);

export const STANDARD_PARAMETER_KEYS = Object.keys(STANDARD_PARAMETERS) as [
  StandardParameterKey,
  ...StandardParameterKey[],
];
/** A platform-owned entry in the authored ordering. */
export interface StandardParameterReference {
  kind: "standard";
  key: StandardParameterKey;
}
export function isStandardParameter(entry: object): entry is StandardParameterReference {
  return "kind" in entry && entry.kind === "standard";
}
export function isAuthoredParameter<T extends object>(entry: T | StandardParameterReference): entry is T {
  return !isStandardParameter(entry);
}
export function mapAuthored<A extends object, B>(
  entries: readonly (A | StandardParameterReference)[],
  fn: (entry: A, index: number) => B,
): (B | StandardParameterReference)[] {
  let index = 0;
  return entries.map((entry) => (isStandardParameter(entry) ? { ...entry } : fn(entry, index++)));
}
export function resolveParameters<T extends object>(
  entries: readonly (T | StandardParameterReference)[],
): (T | (typeof STANDARD_PARAMETERS)[StandardParameterKey])[] {
  return entries.map((entry) => (isStandardParameter(entry) ? STANDARD_PARAMETERS[entry.key] : entry));
}

export function standardParameterNamed(label: string): StandardParameterKey | undefined {
  const normalized = label.trim().toLowerCase();
  return STANDARD_PARAMETER_KEYS.find((key) => STANDARD_PARAMETERS[key].label.toLowerCase() === normalized);
}

/** Validate platform references and their collisions with authored labels. */
export function assertStandardParameters<T extends { id?: string; label: string }>(
  entries: readonly (T | StandardParameterReference)[],
  url: string,
): void {
  const keys = entries.filter(isStandardParameter).map((p) => p.key);
  if (new Set(keys).size !== keys.length || keys.some((key) => !Object.hasOwn(STANDARD_PARAMETERS, key))) {
    throw new InvalidCatalogDefinitionError("Standard parameters must be unique supported keys", {
      url,
      field: "parameters",
    });
  }
  for (const parameter of entries.filter(isAuthoredParameter)) {
    const named = standardParameterNamed(parameter.label);
    if (named && keys.includes(named)) {
      throw new InvalidCatalogDefinitionError("Authored parameter conflicts with an enabled standard", {
        url,
        collection: "parameters",
        memberId: parameter.id,
      });
    }
  }
}
